import { getDistanceMiles } from "./distance";
import {
  buildBroadLocationText,
  looksLikeStreetAddress,
  normalizeBroadLocation,
  parseBroadTypedLocation,
} from "./locationArea";

export const DEFAULT_FALLBACK_LOCATION = {
  lat: 36.1627,
  lng: -86.7816,
  city: "Nashville",
  state: "Tennessee",
  county: "Davidson County",
  locationText: "Nashville, Tennessee",
  locationSource: "skipped-final-fallback",
};

export const LOCATION_SOURCES = {
  EXPLICIT: "explicit",
  SAVED_PROFILE: "saved_profile",
  BROWSER_GPS: "browser_gps",
  FALLBACK: "fallback",
  MISSING: "missing",
};

const STATE_ALIASES = {
  al: "alabama",
  ak: "alaska",
  az: "arizona",
  ar: "arkansas",
  ca: "california",
  co: "colorado",
  ct: "connecticut",
  de: "delaware",
  fl: "florida",
  ga: "georgia",
  hi: "hawaii",
  id: "idaho",
  il: "illinois",
  in: "indiana",
  ia: "iowa",
  ks: "kansas",
  ky: "kentucky",
  la: "louisiana",
  me: "maine",
  md: "maryland",
  ma: "massachusetts",
  mi: "michigan",
  mn: "minnesota",
  ms: "mississippi",
  mo: "missouri",
  mt: "montana",
  ne: "nebraska",
  nv: "nevada",
  nh: "new hampshire",
  nj: "new jersey",
  nm: "new mexico",
  ny: "new york",
  nc: "north carolina",
  nd: "north dakota",
  oh: "ohio",
  ok: "oklahoma",
  or: "oregon",
  pa: "pennsylvania",
  ri: "rhode island",
  sc: "south carolina",
  sd: "south dakota",
  tn: "tennessee",
  tx: "texas",
  ut: "utah",
  vt: "vermont",
  va: "virginia",
  wa: "washington",
  wv: "west virginia",
  wi: "wisconsin",
  wy: "wyoming",
};

const normalizeAreaText = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeStateName = (value = "") => {
  const normalized = normalizeAreaText(value);
  return STATE_ALIASES[normalized] || normalized;
};

const normalizeCountyName = (value = "") =>
  normalizeAreaText(value).replace(/\s+county$/i, "").trim();

const KNOWN_CITY_COUNTIES = {
  tennessee: {
    normandy: "bedford",
    shelbyville: "bedford",
    wartrace: "bedford",
    "bell buckle": "bedford",
    unionville: "bedford",
  },
};

const getAreaParts = (locationData = {}) => {
  const normalized = normalizeLocationRecord(locationData);
  const parsed = parseBroadTypedLocation(
    normalized.locationText || locationData?.locationText || ""
  );
  const city = normalizeAreaText(normalized.city || parsed.city);
  let county = normalizeCountyName(normalized.county || parsed.county);
  const state = normalizeStateName(normalized.state || parsed.state);
  if (!county && state && city) {
    county = KNOWN_CITY_COUNTIES[state]?.[city] || "";
  }
  const locationText = normalizeAreaText(
    [
      normalized.locationText,
      locationData?.locationText,
      locationData?.label,
      locationData?.displayLabel,
    ]
      .filter(Boolean)
      .join(" ")
  );

  return { city, county, state, locationText };
};

const parseCoordinate = (value, min, max) => {
  if (value === "" || value === null || value === undefined) return null;
  const numberValue = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(numberValue)) return null;
  if (numberValue < min || numberValue > max) return null;
  return numberValue;
};

export const normalizeCoordinates = (locationData) => {
  if (!locationData || typeof locationData !== "object") return null;

  const candidates = [
    {
      lat: locationData.lat ?? locationData.latitude,
      lng: locationData.lng ?? locationData.lon ?? locationData.longitude,
    },
    locationData.coords,
    locationData.coordinates,
    locationData.location,
  ].filter((candidate) => candidate && typeof candidate === "object");

  for (const candidate of candidates) {
    const lat = parseCoordinate(candidate.lat ?? candidate.latitude, -90, 90);
    const lng = parseCoordinate(
      candidate.lng ?? candidate.lon ?? candidate.longitude,
      -180,
      180
    );
    if (lat !== null && lng !== null) {
      if (Math.abs(lat) > 70 && Math.abs(lng) < 70) return null;
      return { lat, lng };
    }
  }

  return null;
};

export const hasValidCoordinates = (locationData) =>
  Boolean(normalizeCoordinates(locationData));

export const isDefaultFallbackLocation = (locationData) => {
  const coords = normalizeCoordinates(locationData);
  if (!coords) return false;

  return (
    Math.abs(coords.lat - DEFAULT_FALLBACK_LOCATION.lat) < 0.01 &&
    Math.abs(coords.lng - DEFAULT_FALLBACK_LOCATION.lng) < 0.01
  );
};

export const getLocationLabel = (locationData = {}, fallbackText = "") => {
  if (!locationData || typeof locationData !== "object") {
    const fallback = String(fallbackText || "").trim();
    return looksLikeStreetAddress(fallback) ? "" : fallback;
  }

  const text =
    locationData.locationText ||
    locationData.locationNote ||
    [locationData.city, locationData.state].filter(Boolean).join(", ") ||
    locationData.county ||
    locationData.formattedAddress ||
    fallbackText ||
    "";

  return looksLikeStreetAddress(text) ? "" : String(text).trim();
};

export const normalizeLocationRecord = (locationData = {}, fallbackText = "", sourceHint = "") => {
  const safeLocationData =
    locationData && typeof locationData === "object" ? locationData : {};
  const coords = normalizeCoordinates(locationData);
  const rawLabel = getLocationLabel(safeLocationData, fallbackText);
  const normalized = normalizeBroadLocation(
    {
      ...safeLocationData,
      lat: coords?.lat ?? null,
      lng: coords?.lng ?? null,
      locationText: rawLabel || fallbackText,
    },
    rawLabel || fallbackText
  );
  const locationText =
    buildBroadLocationText(normalized) ||
    rawLabel ||
    String(fallbackText || "").trim();

  return {
    lat: coords?.lat ?? null,
    lng: coords?.lng ?? null,
    city: normalized.city || "",
    state: normalized.state || "",
    county: normalized.county || "",
    label: locationText,
    locationText,
    source: sourceHint || safeLocationData.locationSource || safeLocationData.source || "",
    locationSource: sourceHint || safeLocationData.locationSource || safeLocationData.source || "",
    locationMissing: !coords,
  };
};

export const normalizeProfileLocation = (profileData = {}, options = {}) => {
  if (!profileData || typeof profileData !== "object") return null;

  const { allowFallback = true } = options;
  const nestedLocation =
    profileData.location && typeof profileData.location === "object"
      ? profileData.location
      : {};
  const rootLocation = normalizeLocationRecord({
      lat: profileData.lat,
      lng: profileData.lng,
      latitude: profileData.latitude,
      longitude: profileData.longitude,
      city: profileData.city,
      state: profileData.state,
      county: profileData.county,
      locationText: profileData.locationText,
      locationSource: profileData.locationSource,
    }, "", LOCATION_SOURCES.SAVED_PROFILE);
  const nestedProfileLocation = normalizeLocationRecord(
    nestedLocation,
    profileData.locationText || "",
    LOCATION_SOURCES.SAVED_PROFILE
  );
  const candidates = [
    rootLocation,
    nestedProfileLocation,
    normalizeLocationRecord(
      {
        ...nestedLocation,
        city: nestedLocation.city || profileData.city,
        state: nestedLocation.state || profileData.state,
        county: nestedLocation.county || profileData.county,
        locationText: nestedLocation.locationText || profileData.locationText,
      },
      profileData.locationText || "",
      LOCATION_SOURCES.SAVED_PROFILE
    ),
  ];
  const usableCandidates = candidates.filter((locationData) => {
    if (!locationData.locationText && !hasValidCoordinates(locationData)) return false;
    if (looksLikeStreetAddress(locationData.locationText)) return false;
    if (!allowFallback && isDefaultFallbackLocation(locationData)) return false;
    return true;
  });

  return (
    usableCandidates.find((locationData) => hasValidCoordinates(locationData)) ||
    usableCandidates[0] ||
    null
  );
};

export const selectRecordLocation = ({
  explicitLocation,
  savedProfile,
  browserLocation,
  fallbackLocation,
  allowBrowserLocation = false,
} = {}) => {
  const explicit = explicitLocation ? normalizeLocationRecord(explicitLocation, "", LOCATION_SOURCES.EXPLICIT) : null;
  if (explicit && (explicit.locationText || hasValidCoordinates(explicit))) return explicit;

  const saved = savedProfile ? normalizeProfileLocation(savedProfile) : null;
  if (saved && (saved.locationText || hasValidCoordinates(saved))) return saved;

  const browser = allowBrowserLocation && browserLocation
    ? normalizeLocationRecord(browserLocation, "", LOCATION_SOURCES.BROWSER_GPS)
    : null;
  if (browser && (browser.locationText || hasValidCoordinates(browser))) return browser;

  return fallbackLocation ? normalizeLocationRecord(fallbackLocation, "", LOCATION_SOURCES.FALLBACK) : null;
};

export const selectViewerLocation = ({
  savedProfile,
  explicitLocation,
  browserLocation,
  fallbackLocation,
  allowBrowserLocation = false,
  allowFallback = true,
} = {}) => {
  const explicit = explicitLocation
    ? normalizeLocationRecord(explicitLocation, "", LOCATION_SOURCES.EXPLICIT)
    : null;
  if (explicit && hasValidCoordinates(explicit)) return explicit;

  const saved = savedProfile ? normalizeProfileLocation(savedProfile, { allowFallback: false }) : null;
  if (saved && hasValidCoordinates(saved)) return saved;
  if (saved && (saved.locationText || saved.city || saved.county || saved.state)) return saved;

  const browser =
    allowBrowserLocation && browserLocation
      ? normalizeLocationRecord(browserLocation, "", LOCATION_SOURCES.BROWSER_GPS)
      : null;
  if (browser && hasValidCoordinates(browser)) return browser;

  if (!allowFallback) return normalizeLocationRecord({}, "", LOCATION_SOURCES.MISSING);

  return fallbackLocation
    ? normalizeLocationRecord(fallbackLocation, "", LOCATION_SOURCES.FALLBACK)
    : normalizeLocationRecord({}, "", LOCATION_SOURCES.MISSING);
};

export const getBroadAreaMatch = (viewerLocation, recordLocation) => {
  if (!viewerLocation || !recordLocation) return null;

  const viewer = getAreaParts(viewerLocation);
  const record = getAreaParts(recordLocation);
  if (!viewer.state || !record.state || viewer.state !== record.state) return null;

  if (viewer.county && record.county && viewer.county === record.county) {
    return "county";
  }

  if (viewer.city && record.city && viewer.city === record.city) {
    return "city";
  }

  if (
    viewer.county &&
    record.locationText &&
    record.locationText.includes(viewer.county)
  ) {
    return "county";
  }

  if (
    record.county &&
    viewer.locationText &&
    viewer.locationText.includes(record.county)
  ) {
    return "county";
  }

  if (viewer.city && record.locationText && record.locationText.includes(viewer.city)) {
    return "city";
  }

  if (record.city && viewer.locationText && viewer.locationText.includes(record.city)) {
    return "city";
  }

  return null;
};

export const isBroadAreaMatch = (viewerLocation, recordLocation) =>
  Boolean(getBroadAreaMatch(viewerLocation, recordLocation));

export const buildLocationPayload = (locationData = {}) => {
  const normalized = normalizeLocationRecord(locationData);
  const payload = {
    city: normalized.city || "",
    state: normalized.state || "",
    county: normalized.county || "",
    label: normalized.locationText || "",
    displayLabel: normalized.locationText || "",
    locationText: normalized.locationText || "",
    locationMissing: !hasValidCoordinates(normalized),
  };

  if (normalized.locationSource) {
    payload.locationSource = normalized.locationSource;
    payload.source = normalized.locationSource;
  }

  if (hasValidCoordinates(normalized)) {
    payload.lat = normalized.lat;
    payload.lng = normalized.lng;
  } else {
    payload.lat = null;
    payload.lng = null;
  }

  return payload;
};

export const buildPublicProfileLocationFields = (locationData = {}) => {
  const normalized = normalizeLocationRecord(locationData);
  const locationText = normalized.locationText || "";
  const source =
    normalized.locationSource || normalized.source || LOCATION_SOURCES.MISSING;

  return {
    city: normalized.city || "",
    state: normalized.state || "",
    county: normalized.county || "",
    locationText,
    locationSource: source,
  };
};

export const buildPrivateProfileLocationFields = (locationData = {}) => {
  const payload = buildLocationPayload(locationData);
  const source =
    payload.locationSource ||
    locationData.locationSource ||
    locationData.source ||
    LOCATION_SOURCES.MISSING;

  return {
    ...payload,
    source,
    locationSource: source,
    locationMissing: !hasValidCoordinates(locationData),
  };
};

export const buildCanonicalLocationPayload = (locationData = {}) => {
  const payload = buildLocationPayload(locationData);
  const source =
    payload.source || payload.locationSource || LOCATION_SOURCES.MISSING;

  return {
    ...payload,
    source,
    locationSource: source,
    location: {
      lat: payload.lat ?? null,
      lng: payload.lng ?? null,
      city: payload.city || "",
      county: payload.county || "",
      state: payload.state || "",
      label: payload.label || payload.locationText || "",
      displayLabel: payload.displayLabel || payload.locationText || "",
      locationText: payload.locationText || payload.label || "",
      source,
      locationSource: source,
      locationMissing: payload.locationMissing === true,
    },
  };
};

export const selectCommunityEditLocation = ({
  explicitLocation,
  savedProfile,
  existingRecord,
  preserveExplicitRecordLocation = false,
} = {}) => {
  const explicit = explicitLocation
    ? normalizeLocationRecord(explicitLocation, "", LOCATION_SOURCES.EXPLICIT)
    : null;
  if (explicit && (hasValidCoordinates(explicit) || explicit.locationText)) {
    return explicit;
  }

  const preservedExplicit = preserveExplicitRecordLocation
    ? normalizeLocationRecord(existingRecord, "", LOCATION_SOURCES.EXPLICIT)
    : null;
  if (preservedExplicit && hasValidCoordinates(preservedExplicit)) {
    return preservedExplicit;
  }

  const saved = savedProfile
    ? normalizeProfileLocation(savedProfile, { allowFallback: false })
    : null;
  if (saved && hasValidCoordinates(saved)) return saved;

  const existing = existingRecord ? normalizeLocationRecord(existingRecord) : null;
  if (
    existing &&
    hasValidCoordinates(existing) &&
    existing.locationSource &&
    existing.locationSource !== LOCATION_SOURCES.MISSING
  ) {
    return existing;
  }

  return normalizeLocationRecord(
    { locationText: existing?.locationText || "" },
    existing?.locationText || "",
    LOCATION_SOURCES.MISSING
  );
};

export const selectProductListingLocation = ({
  farmStandLocation,
  sellerProfile,
  existingListing,
} = {}) => {
  const farmStand = farmStandLocation
    ? normalizeLocationRecord(farmStandLocation, "", LOCATION_SOURCES.EXPLICIT)
    : null;
  if (farmStand && hasValidCoordinates(farmStand)) return farmStand;

  const seller = sellerProfile
    ? normalizeProfileLocation(sellerProfile, { allowFallback: false })
    : null;
  if (seller && hasValidCoordinates(seller)) return seller;

  const existing = existingListing ? normalizeLocationRecord(existingListing) : null;
  if (
    existing &&
    hasValidCoordinates(existing) &&
    existing.locationSource &&
    existing.locationSource !== LOCATION_SOURCES.MISSING
  ) {
    return existing;
  }

  return normalizeLocationRecord(
    { locationText: existing?.locationText || "" },
    existing?.locationText || "",
    LOCATION_SOURCES.MISSING
  );
};

export const getSafeDistanceMiles = (viewerLocation, recordLocation) => {
  const viewer = normalizeCoordinates(viewerLocation);
  const record = normalizeCoordinates(recordLocation);
  if (!viewer || !record) return null;
  return getDistanceMiles(viewer.lat, viewer.lng, record.lat, record.lng);
};

export const isWithinRadius = (viewerLocation, recordLocation, radiusMiles) => {
  const distance = getSafeDistanceMiles(viewerLocation, recordLocation);
  if (distance === null) return false;
  return distance <= Number(radiusMiles);
};

const MARKETPLACE_LOCATION_PLACEHOLDERS = new Set([
  "location not listed",
  "location not set",
]);

export const hasMarketplaceLocationFilter = (locationData) => {
  const loc = normalizeLocationRecord(locationData);
  const locationText = String(loc.locationText || "").trim().toLowerCase();

  return Boolean(
    (loc.lat !== null && loc.lat !== undefined) ||
      (loc.lng !== null && loc.lng !== undefined) ||
      loc.city ||
      loc.county ||
      loc.state ||
      (locationText && !MARKETPLACE_LOCATION_PLACEHOLDERS.has(locationText))
  );
};

export const productPassesMarketplaceRadius = (viewerLocation, productLocation, radiusMiles) => {
  const distance = getSafeDistanceMiles(viewerLocation, productLocation);
  if (distance !== null) return distance <= Number(radiusMiles);
  if (isBroadAreaMatch(viewerLocation, productLocation)) return true;
  return (
    !hasMarketplaceLocationFilter(productLocation) ||
    !hasMarketplaceLocationFilter(viewerLocation)
  );
};

export const getLocationDiagnostics = ({
  id = "",
  type = "",
  locationData,
  comparisonLocation,
} = {}) => {
  const normalized = normalizeLocationRecord(locationData);
  const comparison = comparisonLocation ? normalizeLocationRecord(comparisonLocation) : null;
  const distance = comparison ? getSafeDistanceMiles(comparison, normalized) : null;

  return {
    id,
    type,
    displayLabel: normalized.locationText,
    rootLat: locationData?.lat ?? null,
    rootLng: locationData?.lng ?? null,
    nestedLocation: locationData?.location ?? null,
    normalizedLat: normalized.lat,
    normalizedLng: normalized.lng,
    source: normalized.source || normalized.locationSource || LOCATION_SOURCES.MISSING,
    validCoordinates: hasValidCoordinates(normalized),
    comparisonLat: comparison?.lat ?? null,
    comparisonLng: comparison?.lng ?? null,
    computedDistanceMiles: distance,
  };
};
